/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Switch,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import { useEntityTypes, usePublishedForm } from '../../hooks/useCrmQueries'
import { allFields } from '../records/fieldValues'
import { describeFilter } from '../records/listModel'
import FilterBuilder from '../records/FilterBuilder'
import { entityLabel, entityLabelPlural } from '../../utils/helpers'
import { WIDGET_KINDS, WIDTHS, autoTitle } from './dashboardModel'
import WidgetCard from './WidgetCard'

const NUMERIC = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP']
const GROUPABLE = ['SELECT', 'RADIO', 'MULTI_SELECT', 'CHECKBOX', 'LOOKUP', 'USER', 'TEXT', 'EMAIL', 'PHONE', 'URL']
const INTERVALS = [['', 'Automatic'], ['DAY', 'Day'], ['WEEK', 'Week'], ['MONTH', 'Month'], ['QUARTER', 'Quarter'], ['YEAR', 'Year']]

/** Create or edit one widget; the preview runs the real query as you change settings. */
const WidgetEditorDialog = ({ open, initial, isNew, rangeId, onSave, onClose }) => {
  const { entityTypes } = useEntityTypes()
  const [w, setW] = useState(initial)
  const [filterAnchor, setFilterAnchor] = useState(null)
  useEffect(() => { if (open) setW(initial) }, [open, initial])

  const { forms } = useEntityTypes()
  const code = forms.find((f) => f.entityType === w?.entityType)?.code
  const { data: form } = usePublishedForm(code)
  const fields = form ? allFields(form) : []
  const byKey = Object.fromEntries(fields.map((f) => [f.fieldKey, f]))
  const numeric = fields.filter((f) => NUMERIC.includes(f.fieldType))
  const groupable = fields.filter((f) => GROUPABLE.includes(f.fieldType))
  const dates = fields.filter((f) => f.fieldType === 'DATE' || f.fieldType === 'DATETIME')

  if (!w) return null
  const set = (patch) => setW((prev) => ({ ...prev, ...patch }))
  const measureValue = w.fn === 'COUNT' || !w.measureField ? 'COUNT' : `${w.fn}:${w.measureField}`
  const title = w.title || autoTitle(w, byKey, entityLabelPlural(w.entityType || 'RECORD'))
  const complete = w.entityType && form && (w.kind !== 'BAR' || w.groupBy) && (w.kind !== 'TREND' || w.dateField !== 'none')

  const changeEntity = (entityType) => setW((prev) => ({ ...prev, entityType, fn: 'COUNT', measureField: null, groupBy: null, filters: {}, dateField: 'createdOn' }))
  const changeKind = (kind) => {
    if (!kind) return
    set({
      kind,
      width: kind === 'NUMBER' ? 4 : kind === 'TREND' ? 12 : 6,
      groupBy: kind === 'BAR' ? w.groupBy || groupable.find((f) => f.fieldType === 'SELECT')?.fieldKey || groupable[0]?.fieldKey || null : w.groupBy,
      dateField: kind === 'TREND' && w.dateField === 'none' ? 'createdOn' : w.dateField,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isNew ? 'Add widget' : 'Edit widget'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 3, pt: 1 }}>
          <Stack spacing={2}>
            <ToggleButtonGroup exclusive size="small" value={w.kind} onChange={(_, v) => changeKind(v)} fullWidth aria-label="Widget type">
              {WIDGET_KINDS.map((k) => <ToggleButton key={k.kind} value={k.kind}>{k.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: '4px !important' }}>
              {WIDGET_KINDS.find((k) => k.kind === w.kind)?.hint}
            </Typography>

            <TextField select label="Records" value={w.entityType || ''} onChange={(e) => changeEntity(e.target.value)}>
              {entityTypes.map((t) => <MenuItem key={t} value={t}>{entityLabelPlural(t)}</MenuItem>)}
            </TextField>

            <TextField select label={w.kind === 'TABLE' ? 'Rank by' : 'Measure'} value={measureValue}
              onChange={(e) => {
                const [fn, field] = e.target.value.split(':')
                set({ fn, measureField: field || null })
              }}>
              <MenuItem value="COUNT">{w.kind === 'TABLE' ? 'Most recently updated' : `Number of ${entityLabelPlural(w.entityType || 'RECORD').toLowerCase()}`}</MenuItem>
              {numeric.map((f) => <MenuItem key={`SUM:${f.fieldKey}`} value={`SUM:${f.fieldKey}`}>{w.kind === 'TABLE' ? `Highest ${f.label.toLowerCase()}` : `Total ${f.label.toLowerCase()}`}</MenuItem>)}
              {w.kind !== 'TABLE' && numeric.map((f) => <MenuItem key={`AVG:${f.fieldKey}`} value={`AVG:${f.fieldKey}`}>Average {f.label.toLowerCase()}</MenuItem>)}
            </TextField>

            {w.kind === 'BAR' && (
              <TextField select label="Group by" value={w.groupBy || ''} onChange={(e) => set({ groupBy: e.target.value })}
                helperText={groupable.length ? 'Dropdowns keep their option order; other fields sort by value' : 'This record type has no field to group by'}>
                {groupable.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
            )}

            <TextField select label="Date range applies to" value={w.dateField || 'createdOn'} onChange={(e) => set({ dateField: e.target.value })}
              helperText={w.dateField === 'none' ? 'Ignores the dashboard date range (current totals)' : 'The dashboard’s date range filters on this date'}>
              <MenuItem value="createdOn">Created date</MenuItem>
              <MenuItem value="updatedOn">Last updated</MenuItem>
              {dates.map((f) => <MenuItem key={f.fieldKey} value={`data.${f.fieldKey}`}>{f.label}</MenuItem>)}
              {w.kind !== 'TREND' && <MenuItem value="none"><em>Ignore date range</em></MenuItem>}
            </TextField>

            {w.kind === 'TREND' && (
              <TextField select label="Group dates by" value={w.interval || ''} onChange={(e) => set({ interval: e.target.value || null })}
                SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                {INTERVALS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
            )}

            <Box>
              <Button startIcon={<FilterListIcon />} onClick={(e) => setFilterAnchor(e.currentTarget)} disabled={!form}>
                Filters{Object.keys(w.filters || {}).length ? ` (${Object.values(w.filters).reduce((n, o) => n + Object.keys(o).length, 0)})` : ''}
              </Button>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {Object.entries(w.filters || {}).flatMap(([key, ops]) => Object.entries(ops).map(([op, value]) => byKey[key] && (
                  <Chip key={`${key}.${op}`} label={byKey[key].fieldType === 'LOOKUP' ? `${byKey[key].label} is set` : describeFilter(byKey[key], op, value)}
                    onDelete={() => {
                      const filters = { ...w.filters, [key]: { ...w.filters[key] } }
                      delete filters[key][op]
                      if (!Object.keys(filters[key]).length) delete filters[key]
                      set({ filters })
                    }} />
                )))}
              </Box>
              <FilterBuilder anchorEl={filterAnchor} open={!!filterAnchor} onClose={() => setFilterAnchor(null)}
                fields={fields} filters={w.filters || {}} onApply={(filters) => set({ filters })} />
            </Box>

            {w.kind === 'NUMBER' && (
              <FormControlLabel control={<Switch checked={!!w.compare} onChange={(e) => set({ compare: e.target.checked })} disabled={w.dateField === 'none'} />}
                label="Compare with previous period" />
            )}
            {(w.kind === 'BAR' || w.kind === 'TABLE') && (
              <TextField select label={w.kind === 'BAR' ? 'Show at most (rest grouped as Other)' : 'Rows'} value={w.limit || 10} onChange={(e) => set({ limit: Number(e.target.value) })}>
                {[5, 10, 15, 25].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
            )}

            <TextField select label="Width" value={w.width || 4} onChange={(e) => set({ width: Number(e.target.value) })}>
              {WIDTHS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField label="Title" value={w.title || ''} placeholder={title} onChange={(e) => set({ title: e.target.value })}
              helperText="Leave empty to use the suggested title" InputLabelProps={{ shrink: true }} />
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary">Preview</Typography>
            <Box sx={{ mt: 1 }}>
              {complete
                ? <WidgetCard widget={w} title={title} rangeId={rangeId} editing={false} />
                : <Typography variant="body2" color="text.secondary">Choose {w.entityType ? (w.kind === 'BAR' ? 'a field to group by' : 'the settings') : `which ${entityLabel('RECORD').toLowerCase()}s to measure`} to see a preview.</Typography>}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!complete} onClick={() => onSave(w)}>Save widget</Button>
      </DialogActions>
    </Dialog>
  )
}

export default WidgetEditorDialog
